import { cn } from '@helpers/cn';
import { useId, useState, type ChangeEvent } from 'react';

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once every cell is filled, so a form can submit without a click. */
  onComplete?: (value: string) => void;
  length?: number;
  /** The accessible name when no <label htmlFor={id}> points at the input. */
  label?: string;
  /** The id of the error or hint that explains the input. */
  describedBy?: string;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
}

/**
 * One digit per cell, as authenticator apps show it. A single real input
 * sits over the cells, so paste, autofill from SMS/mail and screen readers
 * work as with any text field; the cells only draw its value.
 */
function CodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  label,
  describedBy,
  invalid = false,
  disabled = false,
  autoFocus = false,
  id,
}: CodeInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [focused, setFocused] = useState(false);
  const activeIndex = Math.min(value.length, length - 1);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, length);
    onChange(digits);
    if (digits.length === length && digits !== value) {
      onComplete?.(digits);
    }
  };

  return (
    <div className='eye:relative'>
      <div className='eye:flex eye:gap-8' aria-hidden>
        {Array.from({ length }, (_, index) => (
          <div
            key={index}
            className={cn(
              // Cells shrink, since six full ones overflow a dialog on small phones.
              'eye:flex eye:h-[56px] eye:min-w-0 eye:max-w-[52px] eye:flex-1 eye:items-center eye:justify-center eye:rounded-md eye:border eye:bg-white eye:text-24 eye:font-medium eye:tabular-nums',
              invalid ? 'eye:border-red-100' : 'eye:border-grey-100',
              focused &&
                index === activeIndex &&
                !disabled &&
                'eye:border-black eye:ring-2 eye:ring-black',
              disabled && 'eye:bg-grey-5 eye:text-grey-disabled',
            )}
          >
            {value[index] ?? ''}
          </div>
        ))}
      </div>
      <input
        id={inputId}
        type='text'
        inputMode='numeric'
        autoComplete='one-time-code'
        pattern='[0-9]*'
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        // Transparent, not hidden: it must stay focusable and clickable.
        className='eye:absolute eye:inset-0 eye:w-full eye:cursor-text eye:opacity-0 eye:disabled:cursor-not-allowed'
      />
    </div>
  );
}

export default CodeInput;
